<script setup>
import { ref } from "vue";
import { useCompraBoletosStore } from "@/store/CompraBoletosStore";
import Swal from "sweetalert2";

const emit = defineEmits(["next-step"]);
const store = useCompraBoletosStore();
const errors = ref({});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatearTelefono(numero) {
  const limpio = numero.replace(/\D/g, "");
  if (limpio.length === 10) return `+52${limpio}`;
  if (limpio.length === 12 && limpio.startsWith("52")) return `+${limpio}`;
  if (limpio.length === 13 && limpio.startsWith("521")) return `+${limpio}`;
  return null;
}

function validarCampos() {
  errors.value = {};
  const tel = formatearTelefono(store.comprador.telefono);

  if (!store.comprador.nombre.trim()) errors.value.nombre = true;
  if (!store.comprador.apellido.trim()) errors.value.apellido = true;
  if (!emailRegex.test(store.comprador.email)) errors.value.email = true;
  if (store.comprador.email !== store.comprador.confirmEmail)
    errors.value.confirmEmail = true;
  if (!tel) errors.value.telefono = true;
  else store.comprador.telefono = tel;
  if (!store.comprador.fechaNacimiento) errors.value.fechaNacimiento = true;

  return Object.keys(errors.value).length === 0;
}

function continuar() {
  if (!validarCampos()) {
    Swal.fire({
      icon: "warning",
      title: "Campos incompletos",
      text: "Revisa los campos marcados en rojo antes de continuar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }
  emit("next-step");
}
</script>

<template>
  <form class="step-container" @submit.prevent="continuar">
    <!-- Nombre + Apellido -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Nombre <span class="req">*</span></label>
        <input
          type="text"
          class="field-input"
          :class="{ 'field-error': errors.nombre }"
          v-model="store.comprador.nombre"
          autocomplete="given-name"
          placeholder="Ej. Carlos"
        />
        <span v-if="errors.nombre" class="error-msg">Campo requerido</span>
      </div>
      <div class="field-group">
        <label class="field-label">Apellido <span class="req">*</span></label>
        <input
          type="text"
          class="field-input"
          :class="{ 'field-error': errors.apellido }"
          v-model="store.comprador.apellido"
          autocomplete="family-name"
          placeholder="Ej. García"
        />
        <span v-if="errors.apellido" class="error-msg">Campo requerido</span>
      </div>
    </div>

    <!-- Email + Confirmar email -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Correo electrónico <span class="req">*</span></label>
        <input
          type="email"
          class="field-input"
          :class="{ 'field-error': errors.email }"
          v-model="store.comprador.email"
          autocomplete="email"
          placeholder="correo@ejemplo.com"
        />
        <span v-if="errors.email" class="error-msg">Correo no válido</span>
      </div>
      <div class="field-group">
        <label class="field-label">Confirmar correo <span class="req">*</span></label>
        <input
          type="email"
          class="field-input"
          :class="{ 'field-error': errors.confirmEmail }"
          v-model="store.comprador.confirmEmail"
          autocomplete="email"
          placeholder="Repite tu correo"
        />
        <span v-if="errors.confirmEmail" class="error-msg">Los correos no coinciden</span>
      </div>
    </div>

    <!-- Teléfono + Fecha nacimiento -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Teléfono <span class="req">*</span></label>
        <input
          type="tel"
          class="field-input"
          :class="{ 'field-error': errors.telefono }"
          v-model="store.comprador.telefono"
          autocomplete="tel"
          placeholder="10 dígitos"
        />
        <span v-if="errors.telefono" class="error-msg"
          >Teléfono no válido (10 dígitos)</span
        >
      </div>
      <div class="field-group">
        <label class="field-label">Fecha de nacimiento <span class="req">*</span></label>
        <input
          type="date"
          class="field-input"
          :class="{ 'field-error': errors.fechaNacimiento }"
          v-model="store.comprador.fechaNacimiento"
        />
        <span v-if="errors.fechaNacimiento" class="error-msg">Campo requerido</span>
      </div>
    </div>

    <!-- ¿Eres asistente? -->
    <div class="asistente-row">
      <label class="asistente-label">
        <input
          type="checkbox"
          v-model="store.comprador.esAsistente"
          class="asistente-check"
        />
        <span>
          <strong>Yo también asistiré al evento</strong>
          <span class="asistente-hint">
            — uno de los boletos será tuyo, los demás los compartirás con tus invitados.
          </span>
        </span>
      </label>
    </div>

    <!-- Navegación -->
    <div class="nav-row">
      <a href="/" class="btn-nav btn-secondary-nav">
        <i class="bi bi-house-fill"></i>
        <span class="btn-text">Inicio</span>
      </a>
      <button type="submit" class="btn-nav btn-primary-nav">
        <span class="btn-text">Siguiente</span>
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>
  </form>
</template>

<style scoped>
.step-container {
  padding: 0.5rem 0;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}
.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
}
.req {
  color: #dc3545;
}

.field-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #333;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  background: #fff;
}
.field-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
}
.field-error {
  border-color: #dc3545 !important;
}
.error-msg {
  font-size: 0.71rem;
  color: #dc3545;
}

/* Asistente checkbox */
.asistente-row {
  margin-bottom: 20px;
}
.asistente-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #444;
}
.asistente-check {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #007bff;
  flex-shrink: 0;
}
.asistente-hint {
  font-weight: 400;
  color: #888;
}

/* Navegación */
.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s ease;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover {
  background: #0060df;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}

@media (max-width: 600px) {
  .form-row-2 {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
@media (max-width: 375px) {
  .btn-text {
    display: none;
  }
  .btn-nav {
    justify-content: center;
    padding: 9px 14px;
  }
}
</style>
